import { Component, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { UserProfileStore } from '../store/user-profile.store';
import { ChangePasswordDTO } from '../../../types/user-profile.types';
import { TextInputComponent } from '../../forms/text-input.component';
import { SubmitButtonComponent } from '../../forms/submit-button.component';
import { ErrorAlertComponent } from '../../alerts/error-alert.component';
import { SuccessAlertComponent } from '../../alerts/success-alert.component';
import { Router } from '@angular/router';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    TextInputComponent,
    SubmitButtonComponent,
    ErrorAlertComponent,
    SuccessAlertComponent
],
  selector: 'sb-change-password-form',
  templateUrl: './change-password-form.component.html',
})
export class ChangePasswordFormComponent {
  private userProfileStore = inject(UserProfileStore);
  private router = inject(Router);

  // Form state
  formData = signal<ChangePasswordDTO>({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  formErrors = signal<{ [key: string]: string }>({});
  successMessage = signal<string | null>(null);

  /**
   * Update a form field
   */
  updateFormField(field: keyof ChangePasswordDTO, value: string) {
    this.formData.update((current) => ({ ...current, [field]: value }));
    // Clear error for this field when user starts typing
    if (this.formErrors()[field]) {
      this.formErrors.update((current) => {
        const newErrors = { ...current };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  /**
   * Validate a specific field
   */
  validateField(field: keyof ChangePasswordDTO) {
    const value = this.formData()[field];
    const errors = { ...this.formErrors() };

    switch (field) {
      case 'current_password':
        if (!value.trim()) {
          errors[field] = 'Current password is required';
        } else {
          delete errors[field];
        }
        break;

      case 'new_password':
        if (!value.trim()) {
          errors[field] = 'New password is required';
        } else if (value.length < 8) {
          errors[field] = 'New password must be at least 8 characters';
        } else if (value === this.formData().current_password) {
          errors[field] =
            'New password must be different from current password';
        } else {
          delete errors[field];
        }
        break;

      case 'confirm_password':
        if (!value.trim()) {
          errors[field] = 'Please confirm your new password';
        } else if (value !== this.formData().new_password) {
          errors[field] = 'Passwords do not match';
        } else {
          delete errors[field];
        }
        break;
    }

    this.formErrors.set(errors);
  }

  /**
   * Check if form is valid
   */
  isFormValid(): boolean {
    const data = this.formData();
    return (
      !!data.current_password.trim() &&
      !!data.new_password.trim() &&
      data.new_password.length >= 8 &&
      data.new_password === data.confirm_password &&
      data.new_password !== data.current_password &&
      Object.keys(this.formErrors()).length === 0
    );
  }

  /**
   * Reset form
   */
  resetForm() {
    this.formData.set({
      current_password: '',
      new_password: '',
      confirm_password: '',
    });
    this.formErrors.set({});
    this.successMessage.set(null);
  }

  /**
   * Submit form
   */
  async onSubmit() {
    // Validate all fields
    this.validateField('current_password');
    this.validateField('new_password');
    this.validateField('confirm_password');

    if (!this.isFormValid()) {
      return;
    }

    this.successMessage.set(null);

    try {
      await this.userProfileStore.changePassword(this.formData());
      this.successMessage.set('Password changed successfully!');
      this.resetForm();

      // Clear success message after 5 seconds
      setTimeout(() => {
        this.successMessage.set(null);
        window.location.href = '/auth/login';
      }, 5000);
    } catch (error) {
      // Error is handled by the store
    }
  }

  // Expose store state
  readonly isLoading = this.userProfileStore.isLoading;
  readonly error = this.userProfileStore.error;
}
